// @flow

import flatMap from 'lodash/flatMap';
import fromPairs from 'lodash/fromPairs';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import type { AnyAction } from 'react-redux';
import { combineReducers, type Reducer } from 'redux';
import { createReducer } from 'redux-act';

import type { Currency } from '@gandi/billing';
import {
  roles,
  validForResource,
  type Fqdn,
  type Role,
  type Tld,
  type Process,
} from '@gandi/domain-configuration';
import type { FetchResponse } from '@gandi/resource/redux';

import { setTrusteePrices } from '../../../../modules/app/actions';

import { flattenObject } from '../libs/helpers';

import type {
  State,
  Step,
  FormValues,
  TrusteePrice,
  InnerStep,
  InnerStepFlag,
  ProductDomainInfo,
  PurchaseGroupId,
} from '../types';

import {
  prepareState,
  removeDomainsStart,
  removeDomainsFromWizard,
  removeDomainsSuccess,
  removeDomainsFail,
  incrementCurrentStepIndex,
  saveStepValues,
  setPatchContactsFlag,
  resetState,
  validateContactsStart,
  validateContactsEnd,
  setInnerStepFlag,
  globalCheckResource,
  addTrusteeToRole,
  setTrusteeRoleFlag,
  resetTrusteeRoleFlag,
} from './actions';
import { sortRolesList } from './helpers';

export const initialState: State = {
  isWorking: true,
  steps: [],
  currentStepIndex: 0,
  shouldPatchContacts: false,
  innerSteps: {}, // new wizard step (selection screen) for trustees
  isDeleting: false,
  isWizardReady: false,
  isValidating: false,
  globalErrors: {},
  trusteePrices: undefined,
  trusteeRoleFlag: undefined, // the role for which we need to put a trustee
};

/**
 * Loop over domains and prepare the state like this:
 *
 *  steps: [{
 *    domain: 'step1.fr',
 *    role: 'owner',
 *    status: 'pending',
 *  }, {
 *    domain: 'step1.fr',
 *    role: 'admin',
 *    status: 'pending',
 *  }, { ...
 */
const prepareStateHandler = (
  state: State,
  {
    products,
  }: {
    products: Array<ProductDomainInfo>,
  },
): State => {
  const steps = flatMap(products, product => {
    const { purchaseGroupId, fqdn, tld, phase, process } = product;
    return roles.map(role => ({
      purchaseGroupId,
      domainInfo: { fqdn, tld, phase, process }, // <-- TODO: why do we need this structure in the Step? Remove if possible.
      tld,
      process,
      role,
      status: 'pending',
      values: {},
      errors: {},
    }));
  });

  const innerSteps = fromPairs(
    flatMap(products, product =>
      roles.map(role => [
        // $FlowFixMe[incompatible-type]
        `${product.purchaseGroupId}_${product.tld}_${product.process}_${role}`,
        'menu',
      ]),
    ),
  );

  return {
    ...state,
    // $FlowFixMe[prop-missing]
    // $FlowFixMe[incompatible-return]
    steps,
    currentStepIndex: 0,
    innerSteps,
  };
};

/**
 * Start to remove a domain from the wizard.
 */
const removeDomainsStartHandler = (state: State): State => ({
  ...state,
  isWorking: true,
  isDeleting: true,
});

/**
 * Success to remove a domain from the wizard.
 */
const removeDomainsSuccessHandler = (state: State): State => ({
  ...state,
  isDeleting: false,
});

/**
 * When we successfully get some global errors
 */
const globalCheckResourceSuccessHandler = (
  state: State,
  {
    response,
    purchaseGroupId,
  }: { response: FetchResponse<>, purchaseGroupId: PurchaseGroupId },
): State => {
  if (response.status === 400 && response.data?.errors) {
    return {
      ...state,
      globalErrors: {
        ...state.globalErrors,
        // $FlowFixMe[incompatible-type] TODO: Fix type
        [purchaseGroupId]: response.data.errors,
      },
    };
  }
  return state;
};

/**
 * When we fail to get some global errors
 */
const globalCheckResourceFailureHandler = (
  state: State,
  { purchaseGroupId }: { purchaseGroupId: PurchaseGroupId },
): State => ({
  ...state,
  globalErrors: {
    ...state.globalErrors,
    // $FlowFixMe[incompatible-type] TODO: Fix type
    [purchaseGroupId]: undefined,
  },
  isWizardReady: true,
});

/**
 * When the product has been removed from the cart or added a trustee
 * remove the steps related to a domain,
 * increment the `currentStepIndex` to the first next step in error.
 */
const removeDomainsFromWizardHandler = (
  state: State,
  args: {
    purchaseGroupId: PurchaseGroupId,
    tld: Tld,
    process: Process,
  },
): State => {
  const newSteps = state.steps.filter(
    step =>
      !(
        step.purchaseGroupId === args.purchaseGroupId &&
        step.domainInfo.tld === args.tld &&
        step.domainInfo.process === args.process
      ),
  );

  let newCurrentStepIndex = newSteps.findIndex(
    step => step.status !== 'success',
  );
  if (newCurrentStepIndex === -1) {
    newCurrentStepIndex = newSteps.length;
  }

  return {
    ...state,
    steps: newSteps,
    currentStepIndex: newCurrentStepIndex,
    isWorking: true,
  };
};

/**
 * Fail to remove a domain from the wizard.
 */
const removeDomainsFailHandler = (state: State): State => ({
  ...state,
  isWorking: false,
  isDeleting: false,
});

/**
 * Increment the index to 'go' to the next step.
 */
const incrementCurrentStepIndexHandler = (state: State): State => ({
  ...state,
  currentStepIndex: state.currentStepIndex + 1,
});

/**
 * Save the filled values in the step and as defaultValues to be able
 * to prefill the form of the next steps.
 */
const saveStepValuesHandler = (
  state: State,
  {
    step,
    values,
  }: {
    step: Step,
    values: FormValues,
  },
): State => {
  // if there are multiple roles to validate we use them otherwise we take the one role
  const multiRoles =
    step.roles && step.roles.length > 0 ? step.roles : [step.role];
  const newSteps = state.steps.map(curStep =>
    // we find the steps for the roles that are being validated and we set the values
    curStep.domainInfo.fqdn === step.domainInfo.fqdn &&
    multiRoles.includes(curStep.role)
      ? { ...curStep, values }
      : curStep,
  );

  // don't augment default values for now. (recently raised again by T54574);
  // We should think more about this feature that brings more questions than it solves...
  // const defaultValues = {
  //   ...state.defaultValues,
  //   ...values,
  // };

  return {
    ...state,
    steps: newSteps,
    // defaultValues,
  };
};

/**
 * (Re)Start automatic validation.
 */
const validateContactsStartHandler = (state: State): State => ({
  ...state,
  isWorking: true,
  isWizardReady: true,
  isValidating: true,
});

/**
 * Stop automatic validation when there are some errors.
 */
const validateContactsEndHandler = (state: State): State => ({
  ...state,
  isWorking: false,
  isValidating: false,
});

/**
 * Update the status of the step.
 */
const validForResourceCreateOneStartHandler = (
  state: State,
  resource: { fqdn: Fqdn, role: Role, ... },
): State => {
  const { fqdn, role } = resource;

  const newSteps = state.steps.map(step =>
    step.domainInfo.fqdn === fqdn && step.role === role
      ? {
          ...step,
          status: 'progress',
        }
      : step,
  );

  return {
    ...state,
    steps: newSteps,
  };
};

/**
 * When validation fails for a step,
 * Update the step.
 */
const validForResourceCreateOneFailureHandler = (
  state: State,
  resource: {
    fqdn: Fqdn,
    role: Role,
    error: {
      errors: any,
      response: any,
    },
  },
): State => {
  const {
    fqdn,
    role,
    error: { errors, response },
  } = resource;

  const flattenedErrors = flattenObject(errors);
  const multiRoles = get(response, 'data.errors.0.roles');
  const sortedMultiRoles = multiRoles && sortRolesList(multiRoles);

  const newSteps = state.steps.map(step => {
    if (step.domainInfo.fqdn === fqdn && step.role === role) {
      return {
        ...step,
        errors: flattenedErrors,
        status: 'error',
        // if the error returned the field `roles`, we store it (multiroles validation)
        // Important: do not overwrite `roles` if it is not returned in the error.
        ...(multiRoles !== undefined ? { roles: sortedMultiRoles } : {}),
      };
    }
    return step;
  });

  return {
    ...state,
    // $FlowFixMe[incompatible-return]
    steps: newSteps,
  };
};

/**
 * When validation successes for a step,
 * reset errors and update the status of the step.
 */
const validForResourceCreateOneSuccessHandler = (
  state: State,
  resource: {
    fqdn: Fqdn,
    role: Role,
    roles: Array<Role>,
    values: FormValues,
    ...
  },
): State => {
  const { fqdn, role, values } = resource;

  // 1. if the validation is successful we save the values for the roles and set
  // step status to success.
  const multiRoles = resource.roles || [role];
  const newSteps = state.steps.map(step => {
    if (step.domainInfo.fqdn === fqdn && multiRoles.includes(step.role)) {
      return {
        ...step,
        errors: {},
        status: 'success',
        values,
      };
    }
    return step;
  });

  // 2. then we set the innerSteps to 'hide'
  const {
    purchaseGroupId,
    domainInfo: { tld, process },
  } = newSteps[state.currentStepIndex];

  const newInnerSteps = fromPairs(
    multiRoles.map(multiRole => [
      // $FlowFixMe[incompatible-type]
      `${purchaseGroupId}_${tld}_${process}_${multiRole}`,
      'hide',
    ]),
  );

  return {
    ...state,
    steps: newSteps,
    innerSteps: {
      ...state.innerSteps,
      ...newInnerSteps,
    },
  };
};

const setPatchContactsFlagHandler = (state: State, status: boolean): State => ({
  ...state,
  shouldPatchContacts: status,
});

const setInnerStepFlagHandler = (
  state: State,
  {
    innerStep,
    flag,

    roleHasTrustee,
  }: {
    innerStep: InnerStep,
    flag: InnerStepFlag,
    roleHasTrustee: boolean,
  },
): State => {
  const { purchaseGroupId, tld, process, role } = innerStep;

  let newInnerSteps;

  if (roleHasTrustee) {
    newInnerSteps = {
      [`${purchaseGroupId}_${tld}_${process}_${role}`]: flag,
    };
  } else {
    const allRoles = ['owner', 'admin', 'tech', 'bill'];
    const rolesToChange = allRoles.slice(allRoles.indexOf(role));
    newInnerSteps = fromPairs(
      rolesToChange.map(roleToChange => [
        `${purchaseGroupId}_${tld}_${process}_${roleToChange}`,
        flag,
      ]),
    );
  }

  return {
    ...state,
    innerSteps: {
      ...state.innerSteps,
      ...newInnerSteps,
    },
  };
};

/**
 * Reset the state (when unmounting the wizard).
 */
const resetStateHandler = (): State => initialState;

/**
 * Reset the trustee role flag
 */
const resetTrusteeRoleFlagHandler = (state: State): State => ({
  ...state,
  trusteeRoleFlag: undefined,
});

/**
 * Set the flag for the role to put a trustee on
 */
const setTrusteeRoleFlagHandler = (state: State, role: Role): State => ({
  ...state,
  trusteeRoleFlag: role,
});

/**
 * Adding trustee flag to the role
 */
const addTrusteeToRoleHandler = (
  state: State,
  {
    fqdn,
    role,
    values,
    step,
  }: {
    fqdn: Fqdn,
    role: Role,
    values: FormValues,
    step: Step,
  },
): State => {
  const newSteps = state.steps.map(stateStep => {
    if (stateStep.domainInfo.fqdn === fqdn && stateStep.role === role) {
      return {
        ...stateStep,
        errors: {},
        status: 'success',
        trustee: true,
        values,
      };
    }

    return stateStep;
  });

  // step -> innerStep
  // $FlowFixMe[incompatible-type]
  const innerStep = `${step.purchaseGroupId}_${step.domainInfo.tld}_${step.domainInfo.process}_${step.role}`;

  return {
    ...state,
    steps: newSteps,
    innerSteps: {
      ...state.innerSteps,
      [innerStep]: 'hide',
    },
  };
};

/**
 * Saving trustee prices to state
 */
const setTrusteePricesHandler = (
  state: State,
  payload: {
    currency: Currency,
    prices: Array<TrusteePrice>,
  },
): State => {
  // $FlowFixMe[underconstrained-implicit-instantiation]
  const prices = keyBy(
    payload.prices,
    (price: TrusteePrice) => price.options.extension,
  );

  return {
    ...state,
    trusteePrices: {
      currency: payload.currency,
      prices,
    },
  };
};

// Use in unit test
export const wizardReducer: {
  (): (state: State, payload?: mixed, meta?: mixed) => State,
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<State>,
  options: () => mixed,
  ...
} = createReducer(
  {
    [prepareState.getType()]: prepareStateHandler,
    [removeDomainsStart.getType()]: removeDomainsStartHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [removeDomainsFromWizard.getType()]: removeDomainsFromWizardHandler,
    [removeDomainsSuccess.getType()]: removeDomainsSuccessHandler,
    [removeDomainsFail.getType()]: removeDomainsFailHandler,
    [incrementCurrentStepIndex.getType()]: incrementCurrentStepIndexHandler,
    // $FlowFixMe[incompatible-type] TODO: Fix type
    [setTrusteeRoleFlag]: setTrusteeRoleFlagHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [saveStepValues]: saveStepValuesHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [globalCheckResource.createOne.success.getType()]:
      globalCheckResourceSuccessHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [globalCheckResource.createOne.failure.getType()]:
      globalCheckResourceFailureHandler,
    [validateContactsStart.getType()]: validateContactsStartHandler,
    [validateContactsEnd.getType()]: validateContactsEndHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [validForResource.createOne.start.getType()]:
      validForResourceCreateOneStartHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [validForResource.createOne.failure.getType()]:
      validForResourceCreateOneFailureHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [validForResource.createOne.success.getType()]:
      validForResourceCreateOneSuccessHandler,
    // $FlowFixMe[incompatible-type] TODO: Fix type
    [setPatchContactsFlag.getType()]: setPatchContactsFlagHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [setInnerStepFlag]: setInnerStepFlagHandler,
    [resetState.getType()]: resetStateHandler,
    [resetTrusteeRoleFlag.getType()]: resetTrusteeRoleFlagHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [addTrusteeToRole.getType()]: addTrusteeToRoleHandler,
    // $FlowFixMe[prop-missing] TODO: Fix type
    [setTrusteePrices.getType()]: setTrusteePricesHandler,
  },
  initialState,
);

const reducer: Reducer<State, AnyAction> = combineReducers({
  wizard: wizardReducer,
});

export { reducer };
